import React, { useState } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Paper
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import apiClient from '../../services/api';
import ToastService from '../../core/notifications/ToastService';

interface TimeSlot {
  start_time: string;
  end_time: string;
  capacity: string;
  price_coins: string;
  price_cash: string;
}

export const BulkCreateSlotPage: React.FC = () => {
  const { gymId } = useParams<{ gymId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [daysOfWeek, setDaysOfWeek] = useState<Record<number, boolean>>({
    0: false, // Sunday
    1: true,  // Monday
    2: true,  // Tuesday
    3: true,  // Wednesday
    4: true,  // Thursday
    5: true,  // Friday
    6: false  // Saturday
  });
  const [formData, setFormData] = useState({
    start_date: '',
    end_date: '',
    exclude_dates: ''
  });
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([
    { start_time: '06:00', end_time: '08:00', capacity: '50', price_coins: '400', price_cash: '400' }
  ]);

  const dayLabels = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(null);
  };

  const handleDayToggle = (day: number) => {
    setDaysOfWeek(prev => ({
      ...prev,
      [day]: !prev[day]
    }));
  };

  const handleTimeSlotChange = (index: number, field: keyof TimeSlot, value: string) => {
    const newSlots = [...timeSlots];
    newSlots[index] = {
      ...newSlots[index],
      [field]: value
    };
    setTimeSlots(newSlots);
  };

  const addTimeSlot = () => {
    setTimeSlots([
      ...timeSlots,
      { start_time: '', end_time: '', capacity: '', price_coins: '', price_cash: '' }
    ]);
  };

  const removeTimeSlot = (index: number) => {
    setTimeSlots(timeSlots.filter((_, i) => i !== index));
  };

  const validateForm = (): boolean => {
    if (!formData.start_date || !formData.end_date) {
      setError('Start and end dates are required');
      return false;
    }

    if (new Date(formData.start_date) > new Date(formData.end_date)) {
      setError('End date must be after start date');
      return false;
    }

    if (!Object.values(daysOfWeek).some(v => v)) {
      setError('Select at least one day of the week');
      return false;
    }

    if (timeSlots.length === 0) {
      setError('Add at least one time slot');
      return false;
    }

    for (const slot of timeSlots) {
      if (!slot.start_time || !slot.end_time || !slot.capacity || !slot.price_coins || !slot.price_cash) {
        setError('All time slot fields are required');
        return false;
      }

      if (slot.start_time >= slot.end_time) {
        setError('End time must be after start time in all slots');
        return false;
      }

      if (parseInt(slot.capacity) < 1) {
        setError('Capacity must be at least 1');
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setLoading(true);
      setError(null);

      const selectedDays = Object.entries(daysOfWeek)
        .filter(([_, selected]) => selected)
        .map(([day]) => parseInt(day));

      const excludeDates = formData.exclude_dates
        .split(',')
        .map(d => d.trim())
        .filter(d => d);

      await apiClient.post(`/slots/gyms/${gymId}/slots/bulk`, {
        start_date: formData.start_date,
        end_date: formData.end_date,
        days_of_week: selectedDays,
        time_slots: timeSlots.map(ts => ({
          start_time: ts.start_time,
          end_time: ts.end_time,
          capacity: parseInt(ts.capacity),
          price_coins: parseInt(ts.price_coins),
          price_cash: parseInt(ts.price_cash)
        })),
        exclude_dates: excludeDates
      });

      ToastService.success('Slots created successfully');
      navigate(`/pricing/gyms/${gymId}/slots`);
    } catch (err: any) {
      const message = err.response?.data?.message || 'Failed to create slots';
      setError(message);
      ToastService.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/pricing/gyms/${gymId}/slots`)}
        >
          Back
        </Button>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            Bulk Create Slots
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Create recurring time slots for your gym
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Date Range */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Date Range
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Start Date"
                      name="start_date"
                      value={formData.start_date}
                      onChange={handleDateChange}
                      InputLabelProps={{ shrink: true }}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="date"
                      label="End Date"
                      name="end_date"
                      value={formData.end_date}
                      onChange={handleDateChange}
                      InputLabelProps={{ shrink: true }}
                      required
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Days of Week */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Days of Week
                </Typography>
                <FormGroup row>
                  {dayLabels.map((label, day) => (
                    <FormControlLabel
                      key={day}
                      control={
                        <Checkbox
                          checked={daysOfWeek[day as keyof typeof daysOfWeek]}
                          onChange={() => handleDayToggle(day)}
                        />
                      }
                      label={label}
                    />
                  ))}
                </FormGroup>
              </CardContent>
            </Card>
          </Grid>

          {/* Time Slots */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Time Slots
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={addTimeSlot}
                  >
                    Add Time Slot
                  </Button>
                </Box>

                {timeSlots.map((slot, index) => (
                  <Paper key={index} sx={{ p: 2, mb: 2, backgroundColor: '#f9f9f9' }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={2}>
                        <TextField
                          fullWidth
                          type="time"
                          label="Start Time"
                          value={slot.start_time}
                          onChange={(e) => handleTimeSlotChange(index, 'start_time', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <TextField
                          fullWidth
                          type="time"
                          label="End Time"
                          value={slot.end_time}
                          onChange={(e) => handleTimeSlotChange(index, 'end_time', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Capacity"
                          value={slot.capacity}
                          onChange={(e) => handleTimeSlotChange(index, 'capacity', e.target.value)}
                          inputProps={{ min: 1 }}
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Price (Coins)"
                          value={slot.price_coins}
                          onChange={(e) => handleTimeSlotChange(index, 'price_coins', e.target.value)}
                          inputProps={{ min: 0 }}
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Price (Cash)"
                          value={slot.price_cash}
                          onChange={(e) => handleTimeSlotChange(index, 'price_cash', e.target.value)}
                          inputProps={{ min: 0 }}
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <Button
                          fullWidth
                          variant="outlined"
                          color="error"
                          onClick={() => removeTimeSlot(index)}
                          disabled={timeSlots.length === 1}
                        >
                          Remove
                        </Button>
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
              </CardContent>
            </Card>
          </Grid>

          {/* Exclude Dates */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Exclude Dates (Optional)
                </Typography>
                <TextField
                  fullWidth
                  label="Exclude Dates"
                  name="exclude_dates"
                  value={formData.exclude_dates}
                  onChange={handleDateChange}
                  placeholder="Enter dates separated by commas (e.g., 2026-02-26, 2026-03-15)"
                  multiline
                  rows={2}
                />
                <Typography variant="caption" sx={{ color: '#666', mt: 1, display: 'block' }}>
                  Dates that should be excluded from the recurring pattern
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Actions */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                onClick={() => navigate(`/pricing/gyms/${gymId}/slots`)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{ backgroundColor: '#14B8A6' }}
              >
                {loading ? <CircularProgress size={24} /> : 'Create Slots'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Container>
  );
};

export default BulkCreateSlotPage;
